import { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import "../compnay/ManageCompany.css";

const ALL_COMPANIES = { value: 0, label: "All companies" };

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toFixed(1));

const MarketplaceRiderPerformance = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [companyFilter, setCompanyFilter] = useState(ALL_COMPANIES);
  const [companies, setCompanies] = useState([]);
  const [rows, setRows] = useState([]);

  const companyOptions = useMemo(() => {
    const opts = companies
      .map((c) => {
        const id = c.compID ?? c.CompID;
        if (id == null) return null;
        const name = c.compName || c.CompName || c.name || c.Name || "Company";
        return { value: id, label: `${name} (#${id})` };
      })
      .filter(Boolean);
    return [ALL_COMPANIES, ...opts];
  }, [companies]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_COMPANIES, {
        params: { PageSize: 500, PageNumber: 1, SortBy: "CompName", SortOrder: "ASC" },
      });
      if (!response.data?.status) {
        setCompanies([]);
        return;
      }
      const payload = response.data.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.companies)
          ? payload.companies
          : Array.isArray(payload?.Companies)
            ? payload.Companies
            : [];
      setCompanies(list);
    } catch {
      setCompanies([]);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { FromDate: fromDate, ToDate: toDate };
      const selectedCompId = Number(companyFilter?.value || 0);
      if (selectedCompId > 0) params.CompID = selectedCompId;
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_RIDER_PERFORMANCE, { params });
      if (response.data?.status) setRows(response.data.data || []);
      else showErrorToast(response.data?.message || "Failed to load rider performance");
    } catch {
      showErrorToast("Failed to load rider performance");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, companyFilter?.value]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const btnStyle = useMemo(
    () => ({
      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
      boxShadow: `0 2px 8px ${themeColor}40`,
      border: "none",
      color: "#fff",
      padding: "8px 16px",
      borderRadius: 6,
    }),
    [themeColor]
  );

  return (
    <div className={`manage-company-page ${isDarkMode ? "is-dark" : "is-light"}`}>
      {loading && <Preloader />}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div>
          <h1 className="page-header mb-1">Rider Performance</h1>
          <p className="text-muted mb-0">Go Fatafat rider delivery times, declines, and cancels after assign.</p>
        </div>
        <button type="button" style={btnStyle} onClick={fetchData}>
          Refresh
        </button>
      </div>

      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap gap-3 align-items-end">
          <div style={{ minWidth: 160 }}>
            <FormInput label="From" type="date" value={fromDate} onchange={(e) => setFromDate(e.target.value)} />
          </div>
          <div style={{ minWidth: 160 }}>
            <FormInput label="To" type="date" value={toDate} onchange={(e) => setToDate(e.target.value)} />
          </div>
          <div style={{ minWidth: 260, flex: "1 1 260px", maxWidth: 360 }}>
            <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
              Company
            </label>
            <Select
              value={companyFilter}
              onChange={(opt) => setCompanyFilter(opt || ALL_COMPANIES)}
              options={companyOptions}
              styles={dropdownStyles}
              placeholder="All companies"
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Rider</th>
                  <th>Phone</th>
                  <th>Star</th>
                  <th>Completed</th>
                  <th>Cancel after assign</th>
                  <th>Avg assign→deliver</th>
                  <th>Avg assign→pickup</th>
                  <th>Offers</th>
                  <th>Declines</th>
                  <th>Expired</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-4">
                      No rider activity in range
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.marketplaceRider_ID ?? row.MarketplaceRider_ID}>
                      <td>{row.riderName ?? row.RiderName}</td>
                      <td>{row.phone ?? row.Phone}</td>
                      <td>{row.starLevel ?? row.StarLevel}</td>
                      <td>{row.completedCount ?? row.CompletedCount}</td>
                      <td>{row.cancelAfterAssignCount ?? row.CancelAfterAssignCount}</td>
                      <td>{fmt(row.avgAssignToDeliverMinutes ?? row.AvgAssignToDeliverMinutes)}</td>
                      <td>{fmt(row.avgAssignToPickupMinutes ?? row.AvgAssignToPickupMinutes)}</td>
                      <td>{row.offerCount ?? row.OfferCount}</td>
                      <td>{row.declineCount ?? row.DeclineCount}</td>
                      <td>{row.expiredCount ?? row.ExpiredCount}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceRiderPerformance;
